class WisteriaHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.menu = this.querySelector('[data-mobile-menu]');
    this.searchPanel = this.querySelector('[data-search-panel]');
    this.searchToggles = [...this.querySelectorAll('[data-search-toggle]')];
    this.searchClose = this.querySelector('[data-search-close]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 24);
    this.setSearch = (open) => {
      if (!this.searchPanel) return;
      this.searchPanel.toggleAttribute('hidden', !open);
      this.searchToggles.forEach((button) => button.setAttribute('aria-expanded', String(open)));
      this.classList.toggle('search-open', open);
      if (open) {
        this.menu?.removeAttribute('open');
        this.toggle?.setAttribute('aria-expanded', 'false');
        if (this.toggle) this.toggle.textContent = 'Menu';
        document.body.classList.remove('menu-open');
        requestAnimationFrame(() => this.searchInput?.focus());
      }
    };
    this.onSearchToggle = () => this.setSearch(this.searchPanel?.hasAttribute('hidden'));
    this.onSearchClose = () => this.setSearch(false);
    this.onKeydown = (event) => { if (event.key === 'Escape') this.setSearch(false); };
    this.onToggle = () => {
      this.setSearch(false);
      const open = this.menu.toggleAttribute('open');
      this.toggle.setAttribute('aria-expanded', String(open));
      this.toggle.textContent = open ? 'Close' : 'Menu';
      document.body.classList.toggle('menu-open', open);
    };
    this.toggle?.addEventListener('click', this.onToggle);
    this.searchToggles.forEach((button) => button.addEventListener('click', this.onSearchToggle));
    this.searchClose?.addEventListener('click', this.onSearchClose);
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }
  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    this.searchToggles?.forEach((button) => button.removeEventListener('click', this.onSearchToggle));
    this.searchClose?.removeEventListener('click', this.onSearchClose);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onScroll);
  }
}
customElements.define('wisteria-header', WisteriaHeader);

class WisteriaPromo extends HTMLElement {
  connectedCallback() {
    const expiry = Date.parse(this.dataset.expiry || '');
    if (Number.isFinite(expiry) && Date.now() > expiry) {
      this.remove();
      return;
    }
    this.closeButtons = [...this.querySelectorAll('[data-promo-close]')];
    this.dialog = this.querySelector('.promo-popup__dialog');
    this.close = () => {
      this.classList.remove('is-visible');
      document.body.classList.remove('promo-open');
      window.setTimeout(() => { this.hidden = true; }, 450);
    };
    this.onKeydown = (event) => { if (event.key === 'Escape') this.close(); };
    window.setTimeout(() => {
      this.hidden = false;
      document.body.classList.add('promo-open');
      requestAnimationFrame(() => this.classList.add('is-visible'));
      this.querySelector('.promo-popup__close')?.focus();
    }, Number(this.dataset.delay || 0));
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    document.addEventListener('keydown', this.onKeydown);
  }
  disconnectedCallback() {
    this.closeButtons?.forEach((button) => button.removeEventListener('click', this.close));
    document.removeEventListener('keydown', this.onKeydown);
  }
}
customElements.define('wisteria-promo', WisteriaPromo);

class WisteriaProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.current = this.querySelector('[data-gallery-current]');
    this.previous = this.querySelector('[data-gallery-previous]');
    this.next = this.querySelector('[data-gallery-next]');
    if (!this.track || this.slides.length < 2) return;
    this.index = 0;
    this.onScroll = () => {
      const width = this.track.clientWidth || 1;
      this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
      if (this.current) this.current.textContent = String(this.index + 1);
    };
    this.go = (offset) => {
      const index = (this.index + offset + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
    };
    this.onPrevious = () => this.go(-1);
    this.onNext = () => this.go(1);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.previous?.addEventListener('click', this.onPrevious);
    this.next?.addEventListener('click', this.onNext);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.previous?.removeEventListener('click', this.onPrevious);
    this.next?.removeEventListener('click', this.onNext);
  }
}
customElements.define('wisteria-product-gallery', WisteriaProductGallery);

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-quantity]');
  if (input) input.form?.submit();
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-quantity-button]');
  if (!button) return;
  const quantity = button.closest('.cart-quantity')?.querySelector('[data-cart-quantity]');
  if (!quantity) return;
  const direction = button.dataset.cartQuantityButton === 'increase' ? 1 : -1;
  quantity.value = Math.max(0, Number(quantity.value || 0) + direction);
  quantity.form?.submit();
});

class WisteriaConcierge extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-quiz-step]')];
    this.result = this.querySelector('[data-quiz-result]');
    this.progress = this.querySelector('[data-quiz-progress]');
    this.bars = [...this.querySelectorAll('[data-quiz-bars] span')];
    this.answerList = this.querySelector('[data-quiz-answers]');
    this.recommendations = this.querySelector('[data-quiz-recommendations]');
    this.products = [...this.querySelectorAll('[data-quiz-product]')];
    this.answers = {};
    this.index = 0;
    this.addEventListener('click', (event) => {
      const option = event.target.closest('[data-quiz-option]');
      const back = event.target.closest('[data-quiz-back]');
      const reset = event.target.closest('[data-quiz-reset]');
      if (option) this.choose(option.dataset.quizOption);
      if (back) this.show(Math.max(0, this.index - 1));
      if (reset) this.restart();
    });
    this.show(0);
  }
  choose(value) {
    const key = this.steps[this.index].dataset.quizKey;
    this.answers[key] = value;
    if (this.index < this.steps.length - 1) this.show(this.index + 1);
    else this.finish();
  }
  show(index) {
    this.index = index;
    this.steps.forEach((step, i) => { step.hidden = i !== index; });
    this.result.hidden = true;
    this.bars.forEach((bar, i) => bar.classList.toggle('is-active', i <= index));
    this.progress.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.steps.length).padStart(2, '0')}`;
  }
  finish() {
    this.steps.forEach((step) => { step.hidden = true; });
    this.result.hidden = false;
    this.bars.forEach((bar) => bar.classList.add('is-active'));
    this.answerList.replaceChildren(...Object.values(this.answers).map((answer) => {
      const item = document.createElement('span');
      item.className = 'eyebrow';
      item.textContent = answer;
      return item;
    }));
    this.renderRecommendations();
    this.progress.textContent = 'CURATED FOR YOU';
    this.result.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  restart() {
    this.answers = {};
    this.recommendations.replaceChildren();
    this.show(0);
  }

  renderRecommendations() {
    const ranked = this.products
      .map((product, order) => ({ product, order, score: this.score(product) }))
      .sort((a, b) => b.score - a.score || a.order - b.order)
      .slice(0, 3);

    const cards = ranked.map(({ product }, index) => {
      const card = product.cloneNode(true);
      card.removeAttribute('data-quiz-product');
      const reason = card.querySelector('[data-recommendation-why]');
      if (reason) reason.textContent = this.reason(index);
      return card;
    });
    this.recommendations.replaceChildren(...cards);
  }

  score(product) {
    const title = product.dataset.productTitle || '';
    const collections = (product.dataset.productCollections || '').split(',');
    const price = Number(product.dataset.productPrice || 0);
    const { recipient, occasion, direction, budget } = this.answers;
    let score = 0;

    const collectionWeights = {
      Anniversary: ['love-romance'],
      Birthday: ['celebrations', 'just-because'],
      'Just because': ['just-because', 'love-romance'],
      Sympathy: ['home-living'],
      Celebration: ['celebrations'],
      Wedding: ['weddings'],
      'A partner': ['love-romance'],
      'A parent': ['celebrations', 'home-living'],
      'A friend': ['just-because', 'celebrations'],
      'A colleague': ['corporate-gifting'],
      Myself: ['home-living', 'just-because']
    };
    [...(collectionWeights[occasion] || []), ...(collectionWeights[recipient] || [])].forEach((handle, i) => {
      if (collections.includes(handle)) score += i === 0 ? 36 : 18;
    });

    const wordGroups = {
      'Romantic — blush & cream': ['blush', 'rose', 'pink', 'peony', 'silk', 'lace', 'cascade'],
      'Quiet — ivory & champagne': ['ivory', 'champagne', 'white', 'cream', 'quiet', 'residence'],
      'Joyful — garden colour': ['apricot', 'peach', 'blush', 'sakura', 'garden', 'green'],
      'Elegant — soft neutrals': ['champagne', 'ivory', 'orchid', 'calla', 'manor', 'estate'],
      'Bold — deep & moody': ['crimson', 'velvet', 'mocha', 'sculptural', 'blue']
    };
    (wordGroups[direction] || []).forEach((word) => {
      if (title.includes(word)) score += 8;
    });

    const ranges = {
      'S$ 150 – 250': [15000, 25000],
      'S$ 250 – 400': [25000, 40000],
      'S$ 400 – 600': [40000, 60000],
      'S$ 600+': [60000, Infinity]
    };
    const range = ranges[budget];
    if (range) {
      if (price >= range[0] && price <= range[1]) score += 28;
      else {
        const distance = price < range[0] ? range[0] - price : price - range[1];
        score -= Math.min(24, distance / 2500);
      }
    }

    return score;
  }

  reason(index) {
    const { recipient, occasion, direction, budget } = this.answers;
    const openings = [
      `Our strongest match for ${occasion.toLowerCase()}:`,
      `A considered alternative for ${recipient.toLowerCase()}:`,
      `A final atelier selection within ${budget}:`
    ];
    const directionPhrase = direction === 'Let the atelier choose' ? 'an atelier-led direction' : direction.toLowerCase();
    return `${openings[index]} chosen around ${directionPhrase}.`;
  }
}
customElements.define('wisteria-concierge', WisteriaConcierge);

class WisteriaWeddingFilters extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-wedding-filter]')];
    this.status = this.querySelector('[data-wedding-filter-status]');
    this.buttons.forEach((button) => button.addEventListener('click', () => this.apply(button.dataset.weddingFilter)));
    this.apply('all');
  }

  apply(group) {
    const cards = [...this.parentElement.querySelectorAll('[data-wedding-group]')];
    let visible = 0;
    cards.forEach((card) => {
      const show = group === 'all' || card.dataset.weddingGroup === group;
      card.hidden = !show;
      if (show) visible += 1;
    });
    this.buttons.forEach((button) => {
      const active = button.dataset.weddingFilter === group;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    if (this.status) this.status.textContent = `${visible} ${visible === 1 ? 'composition' : 'compositions'}`;
  }
}
customElements.define('wisteria-wedding-filters', WisteriaWeddingFilters);

class WisteriaProductCustomizer extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-add-to-bag]');
    this.message = this.querySelector('[data-product-form-message]');
    this.messageCard = this.querySelector('[data-message-card]');
    this.messageCount = this.querySelector('[data-word-count]');
    if (this.messageCard) {
      this.onMessageInput = () => this.updateMessageCard();
      this.messageCard.addEventListener('input', this.onMessageInput);
      this.updateMessageCard();
    }
    if (!this.form || !this.dataset.customizationType) return;
    this.onSubmit = (event) => this.submit(event);
    this.form.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.messageCard?.removeEventListener('input', this.onMessageInput);
  }

  updateMessageCard() {
    const limit = Number(this.messageCard?.dataset.wordLimit || 20);
    const words = (this.messageCard?.value || '').trim().split(/\s+/).filter(Boolean);
    if (words.length > limit) this.messageCard.value = words.slice(0, limit).join(' ');
    const count = this.messageCard.value.trim() ? this.messageCard.value.trim().split(/\s+/).length : 0;
    if (this.messageCount) this.messageCount.textContent = `${count} / ${limit} words`;
  }

  async submit(event) {
    event.preventDefault();
    const formData = new FormData(this.form);
    const quantity = Number(formData.get('quantity') || 1);
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value) properties[match[1]] = value;
    }

    const items = [{
      id: Number(formData.get('id')),
      quantity,
      properties
    }];

    this.button.disabled = true;
    this.button.textContent = 'Adding…';
    this.message.hidden = true;
    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error('The selection could not be added.');
      window.location.assign(this.dataset.cartUrl);
    } catch (error) {
      this.button.disabled = false;
      this.button.textContent = 'Add to bag';
      this.message.textContent = error.message;
      this.message.hidden = false;
    }
  }
}
customElements.define('wisteria-product-customizer', WisteriaProductCustomizer);
